// TUT-532 (D24, D26, D27) — os clientes atendidos pelo escritório de
// advocacia (o lado da M&H), o processo aberto pelo Portal do advogado e a
// troca de patrocínio.
import {
  demoDesfazerDesignacao,
  demoDesignacoesDoProcesso,
  demoDesignar,
  demoPanoramaDasDesignacoes,
  demoPortalAdvogadosDoEscritorio,
  demoPortalRenunciar,
} from "../demo/designacoes";
import {
  demoAlterarClienteAtendido,
  demoClientesAtendidos,
  demoPortalClientesAtendidos,
  demoPortalCriarProcesso,
  demoPortalProcessos,
  demoPreviaDoPatrocinio,
  demoTransferirPatrocinio,
} from "../demo/portal-processos";
import type {
  AdvogadosDoEscritorio,
  ConsultaDosClientesAtendidos,
  DesignacoesDoProcesso,
  PaginaDeClientesAtendidos,
  PanoramaDasDesignacoes,
  ClientesAtendidosResponse,
  ConfirmarPatrocinioInput,
  CriarProcessoDoPortalInput,
  PoloDoPatrocinio,
  PreviaDoPatrocinio,
  ProcessoCriadoDoPortal,
  ProcessosDoPortalResponse,
  TransferirPatrocinioInput,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

/** No demo, a recusa do servidor vira a promessa rejeitada com a mesma frase. */
function noDemo<T>(trabalho: () => T): Promise<T> {
  try {
    return Promise.resolve(trabalho());
  } catch (erro) {
    return Promise.reject(erro instanceof Error ? erro : new Error(String(erro)));
  }
}

const id = encodeURIComponent;

export const apiDosProcessosDoPortal = {
  /* ------------------------------ escritório ------------------------------ */
  /**
   * Os clientes que um escritório de advocacia atende (a seção "Clientes
   * atendidos" de Advogados). TUT-570: paginados, com o filtro no servidor.
   */
  clientesAtendidosDoEscritorio(
    counselId: string,
    consulta: ConsultaDosClientesAtendidos = {},
  ): Promise<PaginaDeClientesAtendidos> {
    if (useDemoApi) return noDemo(() => demoClientesAtendidos(counselId, consulta));
    const params = new URLSearchParams();
    if (consulta.busca) params.set("busca", consulta.busca);
    if (consulta.pagina) params.set("pagina", String(consulta.pagina));
    if (consulta.porPagina) params.set("porPagina", String(consulta.porPagina));
    const query = params.toString();
    return request(`/counsels/${id(counselId)}/clientes-atendidos${query ? `?${query}` : ""}`);
  },
  /** TUT-570 — acrescenta UM cliente (só quem gere os cadastros); nunca tira outro. */
  acrescentarClienteAtendido(counselId: string, organizationId: string): Promise<{ atende: number }> {
    if (useDemoApi) return noDemo(() => demoAlterarClienteAtendido(counselId, { acrescentar: organizationId }));
    return request(`/counsels/${id(counselId)}/clientes-atendidos`, {
      method: "POST",
      body: JSON.stringify({ organizationId }),
    });
  },
  /** TUT-570 — tira UM cliente. */
  retirarClienteAtendido(counselId: string, organizationId: string): Promise<{ atende: number }> {
    if (useDemoApi) return noDemo(() => demoAlterarClienteAtendido(counselId, { retirar: organizationId }));
    return request(`/counsels/${id(counselId)}/clientes-atendidos/${id(organizationId)}`, { method: "DELETE" });
  },

  /* ------------------------- designação (TUT-570) ------------------------- */
  /** O bloco "Advogados designados" e a aba Acesso do processo. */
  designacoesDoProcesso(processId: string): Promise<DesignacoesDoProcesso> {
    if (useDemoApi) return noDemo(() => demoDesignacoesDoProcesso(processId));
    return request(`/processes/${id(processId)}/designacoes`);
  },
  /** A M&H designa (escolher já salva, um por vez). */
  designarNoProcesso(processId: string, counselId: string): Promise<DesignacoesDoProcesso> {
    if (useDemoApi) return noDemo(() => demoDesignar(processId, counselId));
    return request(`/processes/${id(processId)}/designacoes`, { method: "POST", body: JSON.stringify({ counselId }) });
  },
  /** A M&H desfaz (imediato, sem confirmação: a aba Acesso guarda). */
  desfazerDesignacao(processId: string, counselId: string): Promise<DesignacoesDoProcesso> {
    if (useDemoApi) return noDemo(() => demoDesfazerDesignacao(processId, counselId));
    return request(`/processes/${id(processId)}/designacoes/${id(counselId)}`, { method: "DELETE" });
  },
  /** Os designados de cada processo com patrocínio: o selo "Sem advogado designado" da lista. */
  panoramaDasDesignacoes(): Promise<PanoramaDasDesignacoes> {
    if (useDemoApi) return noDemo(() => demoPanoramaDasDesignacoes());
    return request("/designacoes/panorama");
  },
  /** Os advogados do escritório de advocacia do login; a pessoa recebe 403. */
  portalAdvogadosDoEscritorio(): Promise<AdvogadosDoEscritorio> {
    if (useDemoApi) return noDemo(() => demoPortalAdvogadosDoEscritorio());
    return request("/portal/advogados-do-escritorio");
  },
  /** O escritório de advocacia designa um advogado dele. */
  portalDesignar(processId: string, counselId: string): Promise<void> {
    if (useDemoApi) return noDemo(() => demoPortalAdvogadosDoEscritorio());
    return request(`/portal/processes/${id(processId)}/designacoes`, { method: "POST", body: JSON.stringify({ counselId }) });
  },
  /** O escritório de advocacia desfaz a designação de um advogado dele. */
  portalDesfazerDesignacao(processId: string, counselId: string): Promise<void> {
    if (useDemoApi) return noDemo(() => demoPortalAdvogadosDoEscritorio());
    return request(`/portal/processes/${id(processId)}/designacoes/${id(counselId)}`, { method: "DELETE" });
  },
  /** O advogado renuncia: perde o acesso na hora, e a M&H é avisada. */
  portalRenunciar(processId: string, motivo: string): Promise<void> {
    if (useDemoApi) return noDemo(() => demoPortalRenunciar(processId));
    return request(`/portal/processes/${id(processId)}/renunciar`, {
      method: "POST",
      body: JSON.stringify(motivo.trim() ? { motivo: motivo.trim() } : {}),
    });
  },
  /**
   * TUT-542 (T9-M1) — o que a troca levaria, para a confirmação listar. Nada
   * muda; as recusas são as da troca.
   */
  previaDoPatrocinio(processId: string, input: TransferirPatrocinioInput): Promise<PreviaDoPatrocinio> {
    if (useDemoApi) return noDemo(() => demoPreviaDoPatrocinio(processId, input));
    const consulta = new URLSearchParams({
      ...(input.counselId ? { counselId: input.counselId } : {}),
      ...(input.semAdvogado ? { semAdvogado: "true" } : {}),
      ...(input.polo ? { polo: input.polo } : {}),
    });
    return request(`/processes/${id(processId)}/patrocinio/previa?${consulta.toString()}`);
  },
  /**
   * "Transferir patrocínio": só a M&H; o escritório de advocacia novo vê o
   * histórico, o antigo deixa de ver. TUT-556 — confirma a prévia: leva a
   * impressão dela, e o 409 diz que o processo mudou desde então.
   */
  transferirPatrocinio(processId: string, input: ConfirmarPatrocinioInput): Promise<{ polo: PoloDoPatrocinio }> {
    if (useDemoApi) return noDemo(() => demoTransferirPatrocinio(processId, input));
    return request(`/processes/${id(processId)}/patrocinio`, { method: "POST", body: JSON.stringify(input) });
  },

  /* -------------------------------- portal -------------------------------- */
  /** Os clientes do "Novo processo": só os que o escritório de advocacia atende. */
  portalClientesAtendidos(): Promise<ClientesAtendidosResponse> {
    if (useDemoApi) return noDemo(() => ({ items: demoPortalClientesAtendidos() }));
    return request("/portal/clientes-atendidos");
  },
  portalProcessos(): Promise<ProcessosDoPortalResponse> {
    if (useDemoApi) return noDemo(() => ({ items: demoPortalProcessos() }));
    return request("/portal/processos");
  },
  /** O processo entra direto (D26); a M&H recebe o aviso. */
  portalCriarProcesso(input: CriarProcessoDoPortalInput): Promise<ProcessoCriadoDoPortal> {
    if (useDemoApi) return noDemo(() => demoPortalCriarProcesso(input));
    return request("/portal/processos", { method: "POST", body: JSON.stringify(input) });
  },
};
